<template>
  <div
    class="catalog-overlay"
    role="dialog"
    aria-modal="true"
    :aria-label="i18n.t('/AccessoryCatalog/CatalogTitle')"
    data-qa="accessory-catalog-overlay"
  >
    <header class="catalog-overlay__top">
      <div>
        <h2>{{ i18n.t('/AccessoryCatalog/CatalogTitle') }}</h2>
        <span v-if="contextLabel" class="catalog-overlay__ctx">{{ contextLabel }}</span>
      </div>
      <div class="catalog-overlay__searchwrap">
        <icon icon="search" class="search-icon" />
        <input
          ref="search"
          v-model="searchTerm"
          type="text"
          class="catalog-overlay__search"
          :aria-label="i18n.t('/AccessoryCatalog/SearchPlaceholder')"
          :placeholder="i18n.t('/AccessoryCatalog/SearchPlaceholder')"
          data-qa="accessory-catalog-search"
        />
        <button
          v-if="searchTerm"
          class="catalog-overlay__searchclear"
          type="button"
          :title="i18n.t('/AccessoryCatalog/ClearSearch')"
          @click="searchTerm = ''"
        >
          <icon :title="i18n.t('/AccessoryCatalog/ClearSearch')" icon="close" />
        </button>
      </div>
      <div class="catalog-overlay__topright">
        <!-- Adding or removing an accessory only changes these two numbers -->
        <div class="catalog-overlay__basket" aria-live="polite" data-qa="accessory-catalog-basket">
          <b>{{ i18n.n(basketTotal, 'currency') }}</b>
          <span>{{ basketCountLabel }} · {{ vatLabel }}</span>
        </div>
        <app-button data-qa="accessory-catalog-done-button" @click="$emit('close')">
          {{ i18n.t('/AccessoryCatalog/Done') }}
        </app-button>
      </div>
    </header>

    <div :class="['catalog-overlay__body', { 'has-detail': !!detailAccessory }]">
      <catalog-filter-rail
        id="accessory-catalog-rail"
        :class="['catalog-overlay__rail', { 'is-open': railOpen }]"
        :tree="tree"
        :counts="counts"
        :selection="selection"
        @select="select"
      />

      <div ref="list" :class="['catalog-overlay__list', { 'is-hidden': railOpen }]">
        <div class="catalog-overlay__listtop">
          <!-- Only rendered where the rail is collapsed; see the 860px breakpoint -->
          <button
            :class="['catalog-overlay__railtoggle', { 'is-on': railOpen }]"
            type="button"
            :aria-expanded="railOpen ? 'true' : 'false'"
            aria-controls="accessory-catalog-rail"
            data-qa="accessory-catalog-rail-toggle"
            @click="railOpen = !railOpen"
          >
            {{ i18n.t('/AccessoryCatalog/CategoriesLabel') }}
          </button>
          <div class="catalog-overlay__crumb">
            {{ crumb }}
            <small>{{ listCountLabel }}</small>
          </div>
          <label class="catalog-overlay__sort">
            {{ i18n.t('/AccessoryCatalog/SortLabel') }}
            <select v-model="sortMode" data-qa="accessory-catalog-sort">
              <option value="categoryName">
                {{ i18n.t('/AccessoryCatalog/SortCategoryName') }}
              </option>
              <option value="priceAsc">{{ i18n.t('/AccessoryCatalog/SortPriceAsc') }}</option>
              <option value="priceDesc">{{ i18n.t('/AccessoryCatalog/SortPriceDesc') }}</option>
            </select>
          </label>
        </div>

        <div v-if="filtered.length === 0" class="catalog-overlay__noresult">
          <h3>{{ i18n.t('/AccessoryCatalog/NoResultsTitle') }}</h3>
          <p>{{ outsideScopeText }}</p>
          <app-button
            v-if="globalHits > 0"
            data-qa="accessory-catalog-show-all"
            @click="showAllCategories"
          >
            {{ showAllResultsText }}
          </app-button>
        </div>

        <template v-else>
          <section
            v-for="group in groups"
            :key="group.name || 'all'"
            class="catalog-overlay__group"
          >
            <div v-if="group.name && groups.length > 1" class="catalog-overlay__grouphead">
              <h3>{{ groupLabel(group) }}</h3>
              <span>{{ groupCountLabel(group) }}</span>
            </div>
            <catalog-list-item
              v-for="item in group.items"
              :key="item.OptionType_ID"
              :accessory="item"
              :quote-row="quoteRowsById[item.OptionType_ID] || null"
              :is-selected="selectedIds.includes(item.OptionType_ID)"
              :is-active="
                !!(detailAccessory && detailAccessory.OptionType_ID === item.OptionType_ID)
              "
              :is-editable="isEditable"
              :vat="vat"
              :now="now"
              @toggle="$emit('toggle', $event)"
              @open="detailAccessory = $event"
            />
          </section>
        </template>

        <!-- Scrolling this into view (or near it) appends the next page -->
        <div ref="sentinel" class="catalog-overlay__sentinel" data-qa="accessory-catalog-sentinel">
          <span v-if="hasMore" class="catalog-overlay__loading">
            {{ i18n.t('/AccessoryCatalog/LoadingMore') }} ({{ filtered.length - visibleCount }}
            {{ i18n.t('/AccessoryCatalog/Remaining') }})
          </span>
        </div>

        <!-- The rail keeps filtering while searching: say what falls outside it -->
        <div
          v-if="!hasMore && outsideScopeCount > 0"
          class="catalog-overlay__outside"
          data-qa="accessory-catalog-outside-scope"
        >
          <span>{{ outsideScopeText }}</span>
          <app-button data-qa="accessory-catalog-show-all" @click="showAllCategories">
            {{ showAllResultsText }}
          </app-button>
        </div>
      </div>

      <accessory-detail-panel
        v-if="detailAccessory"
        :key="detailAccessory.OptionType_ID"
        class="catalog-overlay__detail"
        :accessory="detailAccessory"
        :is-selected="selectedIds.includes(detailAccessory.OptionType_ID)"
        :is-editable="isEditable"
        :vat="vat"
        :now="now"
        @toggle="$emit('toggle', $event)"
        @close="detailAccessory = null"
      />
    </div>
  </div>
</template>

<script>
import Icon from '@/components/generic/presentational/atoms/icon/Icon.vue'
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import CatalogFilterRail from '@/components/generic/presentational/molecules/accessoryCatalog/CatalogFilterRail.vue'
import CatalogListItem from '@/components/generic/presentational/molecules/accessoryCatalog/CatalogListItem.vue'
import AccessoryDetailPanel from '@/components/generic/presentational/molecules/accessoryCatalog/AccessoryDetailPanel.vue'
import catalogClock from '@/mixins/catalogClock'
import modalFocus from '@/mixins/modalFocus'
import {
  QUICK_FILTERS,
  SORTERS,
  buildCategoryTree,
  categoryLabel,
  groupByLevelBelow,
  matchesCategoryPath,
  matchesSearch,
  quickFilterCounts,
  treePath,
} from '@/helpers/ponCatalogFilters'
import { VAT_EXCL, VAT_INCL } from '@/helpers/ponCatalog'

// Dictionary key per quick filter (see QUICK_FILTERS in ponCatalogFilters). The
// crumb spells the filter out in a sentence; the rail wants the short form.
const CRUMB_KEYS = {
  all: '/AccessoryCatalog/CrumbAll',
  action: '/AccessoryCatalog/CrumbAction',
  inStock: '/AccessoryCatalog/CrumbInStock',
  onQuote: '/AccessoryCatalog/CrumbOnQuote',
}
const FILTER_KEYS = {
  all: '/AccessoryCatalog/FilterAll',
  action: '/AccessoryCatalog/FilterAction',
  inStock: '/AccessoryCatalog/FilterInStock',
  onQuote: '/AccessoryCatalog/FilterOnQuote',
}

const PAGE_SIZE = 60
/** Start appending the next page while the sentinel is still this far below the fold */
const PRELOAD_MARGIN = '600px'
/**
 * A keystroke re-filters and re-sorts the whole catalog, and `matchesSearch`
 * strips the HTML description of every accessory it looks at. Typing is faster
 * than that, so the input keeps `searchTerm` (it has to stay responsive) and
 * the list reads `debouncedTerm`, which settles once the user pauses.
 */
const SEARCH_DEBOUNCE_MS = 200

export default {
  components: {
    Icon,
    AppButton,
    CatalogFilterRail,
    CatalogListItem,
    AccessoryDetailPanel,
  },
  mixins: [catalogClock, modalFocus],
  props: {
    /** All catalog accessories (category DealerAccessories) */
    products: {
      type: Array,
      default: () => [],
    },
    /** OptionType_IDs currently on the quote */
    selectedIds: {
      type: Array,
      default: () => [],
    },
    /** The quote's accessory rows, for the basket summary */
    quoteRows: {
      type: Array,
      default: () => [],
    },
    contextLabel: {
      type: String,
      default: '',
    },
    isEditable: {
      type: Boolean,
      default: true,
    },
    /** Which price a single-price line shows: VAT_INCL or VAT_EXCL */
    vat: {
      type: String,
      default: VAT_INCL,
    },
  },
  emits: ['toggle', 'close'],
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      searchTerm: '',
      debouncedTerm: '',
      selection: { kind: 'quick', quick: 'all', path: [] },
      sortMode: 'categoryName',
      visibleCount: PAGE_SIZE,
      detailAccessory: null,
      // Below the rail's breakpoint the filters live behind this toggle
      railOpen: false,
    }
  },
  computed: {
    filterContext() {
      return { now: this.now, selectedIds: this.selectedIds }
    },
    /**
     * Quote rows by OptionType_ID, so a list row can show its own price instead
     * of Autoline's. Only filled under the "Op de offerte" filter: elsewhere the
     * catalog is the subject and the Autoline price stays leading.
     */
    quoteRowsById() {
      if (this.selection.kind !== 'quick' || this.selection.quick !== 'onQuote') return {}
      const byId = {}
      this.quoteRows.forEach((row) => {
        byId[row.OptionType_ID] = row
      })
      return byId
    },
    tree() {
      return buildCategoryTree(this.products)
    },
    counts() {
      return quickFilterCounts(this.products, this.filterContext)
    },
    /**
     * The search pass, kept apart from the scope pass: the "found elsewhere in
     * the catalog" line needs the same set counted without the rail's filter, and
     * searching is by far the expensive half.
     */
    searchHits() {
      if (!this.activeTerm) return this.products
      return this.products.filter((a) => matchesSearch(a, this.activeTerm))
    },
    filtered() {
      const inScope =
        this.selection.kind === 'quick'
          ? (a) => QUICK_FILTERS[this.selection.quick](a, this.filterContext)
          : (a) => matchesCategoryPath(a, this.selection.path)

      return this.searchHits.filter(inScope).sort(SORTERS[this.sortMode])
    },
    hasMore() {
      return this.filtered.length > this.visibleCount
    },
    activeTerm() {
      return this.debouncedTerm.trim()
    },
    globalHits() {
      return this.activeTerm ? this.searchHits.length : 0
    },
    groupingPath() {
      return this.selection.kind === 'category' ? this.selection.path : []
    },
    groups() {
      const page = this.filtered.slice(0, this.visibleCount)
      if (this.sortMode !== 'categoryName') {
        return [{ name: null, items: page }]
      }
      return groupByLevelBelow(page, this.groupingPath)
    },
    groupTotals() {
      // Totals over the full filtered list, so the group header matches the rail counts
      const depth = this.groupingPath.length
      const totals = {}
      this.filtered.forEach((accessory) => {
        const path = treePath(accessory)
        const name = path[depth] || path[path.length - 1]
        totals[name] = (totals[name] || 0) + 1
      })
      return totals
    },
    basketTotal() {
      return this.quoteRows.reduce((sum, row) => sum + Number(row.Price[this.vat] || 0), 0)
    },
    /**
     * Singular and plural are two separate dictionary keys: plugins/i18n.js
     * escapes every `|` in a dictionary value, so vue-i18n's own pluralization
     * would render "one | many" verbatim and never fire.
     */
    basketCountLabel() {
      const count = this.quoteRows.length
      return count === 1
        ? this.i18n.t('/AccessoryCatalog/AccessoryCount', { count })
        : this.i18n.t('/AccessoryCatalog/AccessoryCountPlural', { count })
    },
    /**
     * "n resultaten gevonden" only reads right against a search term; without one
     * the list is simply the catalog, so it gets the plain count.
     */
    listCountLabel() {
      const count = this.filtered.length
      if (this.activeTerm) {
        return count === 1
          ? this.i18n.t('/AccessoryCatalog/ResultCount', { count })
          : this.i18n.t('/AccessoryCatalog/ResultCountPlural', { count })
      }
      return count === 1
        ? this.i18n.t('/AccessoryCatalog/AccessoryCount', { count })
        : this.i18n.t('/AccessoryCatalog/AccessoryCountPlural', { count })
    },
    vatLabel() {
      return this.vat === VAT_EXCL
        ? this.i18n.t('/AccessoryCatalog/ExclVat')
        : this.i18n.t('/AccessoryCatalog/InclVat')
    },
    crumb() {
      if (this.selection.kind === 'category') {
        return this.selection.path.map(categoryLabel).join(' › ')
      }
      return this.i18n.t(CRUMB_KEYS[this.selection.quick])
    },
    /** Short name of the active filter — the crumb does not read well in a sentence */
    scopeLabel() {
      if (this.selection.kind === 'category') {
        return categoryLabel(this.selection.path[this.selection.path.length - 1])
      }
      return this.i18n.t(FILTER_KEYS[this.selection.quick])
    },
    outsideScopeText() {
      return this.i18n.t('/AccessoryCatalog/OutsideScope', { scope: this.scopeLabel })
    },
    /** Hits the search found elsewhere in the catalog, outside the active filter */
    outsideScopeCount() {
      if (!this.activeTerm || this.filtered.length === 0) return 0
      return this.globalHits - this.filtered.length
    },
    showAllResultsText() {
      return this.i18n.t('/AccessoryCatalog/ShowAllResultsShort', { count: this.globalHits })
    },
  },
  watch: {
    selection() {
      this.resetList()
    },
    searchTerm(term) {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.debouncedTerm = term
      }, SEARCH_DEBOUNCE_MS)
    },
    debouncedTerm() {
      this.resetList()
    },
    sortMode() {
      this.resetList()
    },
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown)
    this.startObserving()
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown)
    clearTimeout(this.searchTimer)
    if (this.observer) this.observer.disconnect()
  },
  methods: {
    /** modalFocus override: the search field is what the catalog is opened to use */
    modalFocusFirst() {
      return this.$refs.search
    },
    startObserving() {
      if (typeof IntersectionObserver === 'undefined') return
      if (!this.$refs.list || !this.$refs.sentinel) return
      // Not reactive state: the observer is plumbing, nothing renders from it
      this.observer = new IntersectionObserver(this.onSentinelVisible, {
        root: this.$refs.list,
        rootMargin: `${PRELOAD_MARGIN} 0px`,
      })
      this.observer.observe(this.$refs.sentinel)
    },
    onSentinelVisible(entries) {
      if (!entries.some((entry) => entry.isIntersecting)) return
      if (!this.hasMore) return
      this.visibleCount += PAGE_SIZE
      this.rearmObserver()
    },
    rearmObserver() {
      // An observer only reports *changes* in intersection, so a sentinel that
      // stays in view after appending a page would never fire again. Re-observing
      // re-evaluates it, which keeps paging until the list fills the viewport.
      this.$nextTick(() => {
        if (!this.observer || !this.$refs.sentinel) return
        this.observer.unobserve(this.$refs.sentinel)
        this.observer.observe(this.$refs.sentinel)
      })
    },
    onKeydown(event) {
      if (event.key !== 'Escape') return
      // Esc closes the detail panel first, then the overlay
      if (this.detailAccessory) {
        this.detailAccessory = null
      } else {
        this.$emit('close')
      }
    },
    select(selection) {
      this.selection = selection
      // Where the rail is a panel over the list, picking a filter means "show me"
      this.railOpen = false
    },
    showAllCategories() {
      this.select({ kind: 'quick', quick: 'all', path: [] })
    },
    resetList() {
      this.visibleCount = PAGE_SIZE
      if (this.$refs.list) this.$refs.list.scrollTop = 0
      this.rearmObserver()
    },
    groupLabel(group) {
      return categoryLabel(group.name)
    },
    groupCountLabel(group) {
      const total = this.groupTotals[group.name] || group.items.length
      const shown = group.items.length
      return shown < total
        ? this.i18n.t('/AccessoryCatalog/GroupCount', { shown, total })
        : `${total}`
    },
  },
}
</script>

<style lang="scss" scoped>
// The group heads stick directly under the toolbar, so their offset is the
// toolbar's height. Deriving both from the same parts keeps them together when
// the toolbar's padding or control height changes.
$toolbar-padding-y: 12px;
$toolbar-control-height: 34px;
$toolbar-border-width: 1px;
$toolbar-height: $toolbar-padding-y * 2 + $toolbar-control-height + $toolbar-border-width;

// The select's arrow is drawn in the same ink as its text. A data URI ends at a
// literal '#', so the token's hex has to arrive percent-encoded.
$select-arrow-ink: '%23' + str-slice('#{$text-color}', 2);

.catalog-overlay {
  position: fixed;
  inset: 0;
  z-index: z-index(modal);
  background: $white;
  display: grid;
  grid-template-rows: auto 1fr;
  animation: overlay-fade 0.16s ease;

  &__top {
    display: grid;
    grid-template-columns: 268px 1fr auto;
    align-items: center;
    gap: 20px;
    padding: 13px 22px;
    border-bottom: 1px solid $gray-light;
    background: $gray-lightest;

    h2 {
      margin: 0;
      font-size: 1.3em;
      font-weight: 700;
      color: var(--color-primary);
      line-height: 1.15;
    }
  }

  &__ctx {
    display: block;
    font-size: 12px;
    color: $gray-darker;
    margin-top: 2px;
  }

  &__searchwrap {
    position: relative;
    max-width: 560px;
    width: 100%;

    svg.search-icon {
      @include icon;
      position: absolute;
      left: 14px;
      top: 50%;
      transform: translateY(-50%);
      width: 16px;
      height: 16px;
      color: $gray-dark;
    }
  }

  &__search {
    height: 42px;
    width: 100%;
    padding: 0 38px 0 40px;
    border: 1.5px solid $gray-light;
    border-radius: 2em;
    background: $white;
    font-family: inherit;
    font-size: 14.5px;
    color: $text-color;

    &:focus {
      outline: none;
      border-color: var(--color-primary);
    }
  }

  &__searchclear {
    position: absolute;
    right: 6px;
    top: 50%;
    transform: translateY(-50%);
    width: 30px;
    height: 30px;
    border: 0;
    border-radius: 50%;
    background: none;
    color: $gray-dark;
    display: grid;
    place-items: center;
    cursor: pointer;

    &:hover {
      background: $gray-lightest;
      color: $text-color;
    }

    svg {
      @include icon;
      width: 13px;
      height: 13px;
    }
  }

  &__topright {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  &__basket {
    text-align: right;
    line-height: 1.2;

    b {
      display: block;
      font-size: 1.05em;
      font-weight: 700;
      color: var(--color-primary);
      font-variant-numeric: tabular-nums;
    }

    span {
      font-size: 11.5px;
      color: $gray-darker;
    }
  }

  &__body {
    display: grid;
    grid-template-columns: 268px minmax(0, 1fr);
    min-height: 0;

    &.has-detail {
      grid-template-columns: 268px minmax(0, 1fr) 452px;
    }
  }

  &__rail {
    border-right: 1px solid $gray-light;
  }

  &__list {
    overflow-y: auto;
    padding: 0 0 40px;
    min-width: 0;
  }

  &__listtop {
    position: sticky;
    top: 0;
    z-index: 4;
    display: flex;
    align-items: center;
    gap: 14px;
    padding: $toolbar-padding-y 24px;
    background: $white;
    border-bottom: $toolbar-border-width solid $gray-lightest;
  }

  &__railtoggle {
    // Only where the rail itself is collapsed away
    display: none;
    align-items: center;
    height: $toolbar-control-height;
    padding: 0 14px;
    border: 1px solid $gray-light;
    border-radius: 2em;
    background: $white;
    font-family: inherit;
    font-size: 13px;
    color: $text-color;
    cursor: pointer;

    &.is-on {
      border-color: var(--color-primary);
      color: var(--color-primary);
    }
  }

  &__crumb {
    font-size: 1.05em;
    font-weight: 700;
    color: var(--color-primary);

    small {
      font-weight: 400;
      color: $gray-dark;
      font-size: 13px;
      margin-left: 8px;
    }
  }

  &__sort {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: $gray-darker;

    select {
      height: $toolbar-control-height;
      border: 1px solid $gray-light;
      border-radius: 2em;
      padding: 0 30px 0 13px;
      font-family: inherit;
      font-size: 13px;
      color: $text-color;
      background: $white
        url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'><path d='M2 4l4 4 4-4' stroke='#{$select-arrow-ink}' stroke-width='1.5' fill='none' stroke-linecap='round' stroke-linejoin='round'/></svg>")
        no-repeat right 11px center;
      appearance: none;
      cursor: pointer;
    }
  }

  &__grouphead {
    position: sticky;
    top: $toolbar-height;
    z-index: 3;
    display: flex;
    align-items: baseline;
    gap: 10px;
    padding: 16px 24px 8px;
    background: linear-gradient($white 78%, rgba($white, 0));

    h3 {
      margin: 0;
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: var(--color-primary);
    }

    span {
      font-size: 12px;
      color: $gray-dark;
    }
  }

  &__noresult {
    padding: 60px 24px;
    text-align: center;
    color: $gray-darker;

    h3 {
      color: var(--color-primary);
      font-size: 1.15em;
      margin: 0 0 8px;
    }

    p {
      margin-bottom: 1em;
    }
  }

  &__outside {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 26px 24px 8px;
    text-align: center;

    span {
      font-size: 13px;
      color: $gray-darker;
    }
  }

  &__sentinel {
    display: flex;
    justify-content: center;
    min-height: 1px;
  }

  &__loading {
    padding: 22px 24px 8px;
    font-size: 13px;
    color: $gray-dark;
  }

  &__detail {
    border-left: 1px solid $gray-light;
  }

  @media (max-width: 1280px) {
    &__body.has-detail {
      grid-template-columns: 268px minmax(0, 1fr) 400px;
    }
  }

  @media (max-width: 1100px) {
    &__body,
    &__body.has-detail {
      grid-template-columns: 232px minmax(0, 1fr);
    }

    &__body.has-detail &__detail {
      position: fixed;
      right: 0;
      top: 68px;
      bottom: 0;
      width: min(440px, 92vw);
      z-index: 12;
      box-shadow: $shadow-lg;
    }

    &__top {
      grid-template-columns: 232px 1fr auto;
    }
  }

  @media (max-width: 860px) {
    &__top {
      grid-template-columns: 1fr auto;
      gap: 12px;
      row-gap: 10px;
    }

    &__searchwrap {
      grid-column: 1 / -1;
      max-width: none;
      order: 3;
    }

    &__body,
    &__body.has-detail {
      grid-template-columns: 1fr;
    }

    // There is no room for the rail beside the list, so it becomes a panel the
    // list makes way for. Hiding it outright took the quick filters with it and
    // left the breadcrumb naming a scope the user could no longer leave.
    &__rail {
      display: none;
      border-right: 0;

      &.is-open {
        display: block;
      }
    }

    &__list.is-hidden {
      display: none;
    }

    &__railtoggle {
      display: inline-flex;
    }
  }
}

@keyframes overlay-fade {
  from {
    opacity: 0;
    transform: translateY(8px);
  }

  to {
    opacity: 1;
    transform: none;
  }
}
</style>
